/**
 * Pure form logic for the admin UI: form state <-> contract payloads, validation with the contract
 * schemas, and mapping of validation issues (client or server) onto field keys.
 */
import type {
  DiveObject,
  Difficulty,
  I18nText,
  Locale,
  ObjectType,
  SpotDetail,
  SpotRatings,
  Status,
} from "@divespots/contracts";
import {
  DiveObjectCreate,
  DiveObjectUpdate,
  LOCALES,
  SpotCreate,
  SpotUpdate,
} from "@divespots/contracts";

export const TEXT_KEYS = [
  "description",
  "access",
  "parking",
  "entry",
  "hazards",
  "restrictions",
  "facilities",
] as const;
export type TextKey = (typeof TEXT_KEYS)[number];

export const RATING_KEYS = [
  "fish",
  "walls",
  "caves",
  "wrecksSculptures",
  "current",
  "surroundings",
] as const;
export type RatingKey = keyof SpotRatings;

export type LocaleTexts = Record<Locale, string>;

export const emptyTexts = (): LocaleTexts => ({ de: "", en: "", fr: "", it: "" });

export function toLocaleTexts(t: I18nText | undefined | null): LocaleTexts {
  const out = emptyTexts();
  for (const l of LOCALES) out[l] = t?.[l] ?? "";
  return out;
}

/** Only non-blank translations go on the wire. */
export function toI18nText(t: LocaleTexts): I18nText {
  const out: I18nText = {};
  for (const l of LOCALES) if (t[l].trim() !== "") out[l] = t[l];
  return out;
}

export interface ClosedPeriodForm {
  from: string;
  to: string;
  reason: LocaleTexts;
}

export interface SpotForm {
  name: string;
  slug: string;
  lat: string;
  lon: string;
  hasParking: boolean;
  parkLat: string;
  parkLon: string;
  waterBody: string;
  locality: string;
  difficulty: Difficulty | "";
  maxDepthM: string;
  closedPeriods: ClosedPeriodForm[];
  /** Closed for good (staff only): toggle, optional day (YYYY-MM-DD) and reason per language. */
  permanentClosed: boolean;
  closedSince: string;
  closedReason: LocaleTexts;
  status: Status;
  ratings: Record<RatingKey, number | null>;
  texts: Record<TextKey, LocaleTexts>;
}

export function emptySpotForm(): SpotForm {
  return {
    name: "",
    slug: "",
    lat: "",
    lon: "",
    hasParking: false,
    parkLat: "",
    parkLon: "",
    waterBody: "",
    locality: "",
    difficulty: "",
    maxDepthM: "",
    closedPeriods: [],
    permanentClosed: false,
    closedSince: "",
    closedReason: emptyTexts(),
    status: "draft",
    ratings: {
      fish: null,
      walls: null,
      caves: null,
      wrecksSculptures: null,
      current: null,
      surroundings: null,
    },
    texts: Object.fromEntries(TEXT_KEYS.map((k) => [k, emptyTexts()])) as Record<
      TextKey,
      LocaleTexts
    >,
  };
}

const num = (v: number) => String(v);

export function spotToForm(s: SpotDetail): SpotForm {
  return {
    name: s.name,
    slug: s.slug,
    lat: num(s.location.lat),
    lon: num(s.location.lon),
    hasParking: s.parkingLocation !== null,
    parkLat: s.parkingLocation ? num(s.parkingLocation.lat) : "",
    parkLon: s.parkingLocation ? num(s.parkingLocation.lon) : "",
    waterBody: s.waterBody,
    locality: s.locality,
    difficulty: s.difficulty ?? "",
    maxDepthM: s.maxDepthM === null ? "" : num(s.maxDepthM),
    closedPeriods: s.closedPeriods.map((p) => ({
      from: p.from,
      to: p.to,
      reason: toLocaleTexts(p.reason),
    })),
    permanentClosed: s.permanentClosure !== null,
    closedSince: s.permanentClosure?.since ?? "",
    closedReason: toLocaleTexts(s.permanentClosure?.reason),
    status: s.status,
    ratings: { ...s.ratings },
    texts: Object.fromEntries(TEXT_KEYS.map((k) => [k, toLocaleTexts(s[k])])) as Record<
      TextKey,
      LocaleTexts
    >,
  };
}

/** A coordinate pair from two text inputs; `null` when either is empty or not a number. */
export function parseLatLon(lat: string, lon: string): { lat: number; lon: number } | null {
  if (lat.trim() === "" || lon.trim() === "") return null;
  const la = Number(lat.replace(",", "."));
  const lo = Number(lon.replace(",", "."));
  return Number.isFinite(la) && Number.isFinite(lo) ? { lat: la, lon: lo } : null;
}

const parseNumber = (v: string): number | null | undefined => {
  if (v.trim() === "") return null;
  const n = Number(v.replace(",", "."));
  return Number.isFinite(n) ? n : undefined;
};

/** The SpotCreate input for a form. `undefined` pieces fail schema validation with a path. */
export function formToSpotInput(f: SpotForm): Record<string, unknown> {
  const maxDepth = parseNumber(f.maxDepthM);
  return {
    name: f.name,
    ...(f.slug.trim() === "" ? {} : { slug: f.slug.trim() }),
    location: parseLatLon(f.lat, f.lon) ?? { lat: undefined, lon: undefined },
    parkingLocation: f.hasParking
      ? (parseLatLon(f.parkLat, f.parkLon) ?? { lat: undefined, lon: undefined })
      : null,
    waterBody: f.waterBody.trim(),
    locality: f.locality.trim(),
    difficulty: f.difficulty === "" ? null : f.difficulty,
    maxDepthM: maxDepth === undefined ? "not-a-number" : maxDepth,
    closedPeriods: f.closedPeriods.map((p) => {
      const reason = toI18nText(p.reason);
      return {
        from: p.from.trim(),
        to: p.to.trim(),
        ...(Object.keys(reason).length ? { reason } : {}),
      };
    }),
    permanentClosure: f.permanentClosed
      ? {
          since: f.closedSince.trim() === "" ? null : f.closedSince.trim(),
          reason: toI18nText(f.closedReason),
        }
      : null,
    status: f.status,
    ratings: { ...f.ratings },
    ...Object.fromEntries(TEXT_KEYS.map((k) => [k, toI18nText(f.texts[k])])),
  };
}

export interface Issue {
  path: readonly (string | number | symbol)[];
  message: string;
  code?: string;
}

export type FieldErrors = Record<string, string>;

/** `location.lat` style keys; the first issue per key wins. */
export function issuesToErrors(
  issues: readonly Issue[],
  t: (key: string) => string,
  /** Issues that come from the server: its message is shown on the field when it has one. */
  fromServer = false,
): FieldErrors {
  const out: FieldErrors = {};
  for (const i of issues) {
    const key = i.path.map(String).join(".") || "_";
    const serverText = fromServer && typeof i.message === "string" ? i.message.trim() : "";
    if (!(key in out)) out[key] = serverText || issueText(i, t);
  }
  return out;
}

function issueText(i: Issue, t: (key: string) => string): string {
  const last = String(i.path[i.path.length - 1] ?? "");
  if (last === "slug") return t("admin.err.slug");
  if (last === "from" || last === "to") return t("admin.err.mmdd");
  if (/translation/i.test(i.message)) return t("admin.err.nameRequired");
  if (last === "lat" || last === "lon") return t("admin.err.coords");
  if (i.code === "too_small" && last === "name") return t("admin.err.required");
  if (i.code === "custom" && /different|equal|override/i.test(i.message)) return i.message;
  return t("admin.err.invalid");
}

export type Validation<T> = { ok: true; data: T } | { ok: false; errors: FieldErrors };

export function validateSpotCreate(
  f: SpotForm,
  t: (key: string) => string,
): Validation<SpotCreate> {
  const parsed = SpotCreate.safeParse(formToSpotInput(f));
  if (parsed.success) return { ok: true, data: formToSpotInput(f) as SpotCreate };
  return { ok: false, errors: issuesToErrors(parsed.error.issues, t) };
}

/**
 * PATCH body: only the top-level keys that changed (each is replaced whole on the server, so
 * `ratings` and every changed array/object are sent complete). `{}` means nothing to save.
 */
export function spotPatch(original: SpotDetail, f: SpotForm): Record<string, unknown> {
  const before = formToSpotInput(spotToForm(original));
  const after = formToSpotInput(f);
  const patch: Record<string, unknown> = {};
  for (const k of Object.keys(after)) {
    if (k === "slug" && f.slug.trim() === "") continue;
    if (JSON.stringify(before[k]) !== JSON.stringify(after[k])) patch[k] = after[k];
  }
  return patch;
}

export function validateSpotPatch(
  original: SpotDetail,
  f: SpotForm,
  t: (key: string) => string,
): Validation<Record<string, unknown>> {
  const patch = spotPatch(original, f);
  const parsed = SpotUpdate.safeParse(patch);
  if (parsed.success) return { ok: true, data: patch };
  return { ok: false, errors: issuesToErrors(parsed.error.issues, t) };
}

// ---------------------------------------------------------------- objects

export interface ObjectForm {
  type: ObjectType;
  name: LocaleTexts;
  description: LocaleTexts;
  lat: string;
  lon: string;
  /** "Gemessene Tiefe"; empty = use the computed depth. */
  override: string;
  status: Status;
}

export function emptyObjectForm(at?: { lat: number; lon: number }): ObjectForm {
  return {
    type: "figure",
    name: emptyTexts(),
    description: emptyTexts(),
    lat: at ? num(at.lat) : "",
    lon: at ? num(at.lon) : "",
    override: "",
    status: "draft",
  };
}

export function objectToForm(o: DiveObject): ObjectForm {
  return {
    type: o.type,
    name: toLocaleTexts(o.name),
    description: toLocaleTexts(o.description),
    lat: num(o.location.lat),
    lon: num(o.location.lon),
    override: o.depthOverrideM === null ? "" : num(o.depthOverrideM),
    status: o.status,
  };
}

export function formToObjectInput(f: ObjectForm): Record<string, unknown> {
  const override = parseNumber(f.override);
  return {
    type: f.type,
    name: toI18nText(f.name),
    description: toI18nText(f.description),
    location: parseLatLon(f.lat, f.lon) ?? { lat: undefined, lon: undefined },
    depthOverrideM: override === undefined ? "not-a-number" : override,
    status: f.status,
  };
}

export function validateObjectCreate(
  spotId: string,
  f: ObjectForm,
  t: (key: string) => string,
): Validation<Record<string, unknown>> {
  const input = { spotId, ...formToObjectInput(f) };
  const parsed = DiveObjectCreate.safeParse(input);
  if (parsed.success) return { ok: true, data: input };
  return { ok: false, errors: issuesToErrors(parsed.error.issues, t) };
}

export function objectPatch(original: DiveObject, f: ObjectForm): Record<string, unknown> {
  const before = formToObjectInput(objectToForm(original));
  const after = formToObjectInput(f);
  const patch: Record<string, unknown> = {};
  for (const k of Object.keys(after)) {
    if (JSON.stringify(before[k]) !== JSON.stringify(after[k])) patch[k] = after[k];
  }
  return patch;
}

export function validateObjectPatch(
  original: DiveObject,
  f: ObjectForm,
  t: (key: string) => string,
): Validation<Record<string, unknown>> {
  const patch = objectPatch(original, f);
  const parsed = DiveObjectUpdate.safeParse(patch);
  if (parsed.success) return { ok: true, data: patch };
  return { ok: false, errors: issuesToErrors(parsed.error.issues, t) };
}

/** Which locales still lack a translation where `de` has text (for the tab markers). */
export function missingLocales(fields: readonly LocaleTexts[]): Locale[] {
  return LOCALES.filter(
    (l) => l !== "de" && fields.some((f) => f.de.trim() !== "" && f[l].trim() === ""),
  );
}

// ---------------------------------------------------------------- unsaved and concurrent edits

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/** True when the form differs from the form of the record as loaded. */
export const isFormDirty = (form: unknown, baseline: unknown): boolean => !same(form, baseline);

/**
 * After a successful save, take the server's values into the form. A field keeps what the person typed
 * since submitting (`current` differs from `snapshot`); every other field shows the server's value.
 * `foreign` is true when a field the save did not send (`sent` equals `before`) nevertheless differs
 * on the server: somebody else changed it in the meantime.
 */
export function mergeSaved(args: {
  current: SpotForm;
  snapshot: SpotForm;
  sent: SpotForm;
  before: SpotForm;
  after: SpotForm;
}): { form: SpotForm; foreign: boolean } {
  const { current, snapshot, sent, before, after } = args;
  let foreign = false;
  const pick = <T>(cur: T, snap: T, snt: T, bef: T, aft: T): T => {
    if (same(snt, bef) && !same(aft, bef)) foreign = true;
    return same(cur, snap) ? aft : cur;
  };
  const out: Record<string, unknown> = { ...current };
  for (const k of Object.keys(current) as (keyof SpotForm)[]) {
    if (k === "texts") continue;
    out[k] = pick(current[k], snapshot[k], sent[k], before[k], after[k]);
  }
  out.texts = Object.fromEntries(
    TEXT_KEYS.map((k) => [
      k,
      pick(current.texts[k], snapshot.texts[k], sent.texts[k], before.texts[k], after.texts[k]),
    ]),
  );
  return { form: out as unknown as SpotForm, foreign };
}
